import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import WeeklyScheduleGrid from '@/components/WeeklyScheduleGrid';
import { useWeeklyScheduleBase } from '@/hooks/useWeeklyScheduleBase';
import { fetchShops } from '@/store/slices/shopSlice';
import { Shop } from '@/types/shop';
import { getWeekStart } from '@/utils/schedule';

const ROOM_COLOR_CONFIG = [
  { filterActive: 'bg-[#2563EB] text-white' },
  { filterActive: 'bg-teal-500 text-white' },
  { filterActive: 'bg-purple-500 text-white' },
];


function formatDateRange(weekStart: Date) {
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);
  const fmt = (d: Date) => `${d.getMonth() + 1}월 ${d.getDate()}일`;
  return `${fmt(weekStart)} ~ ${fmt(end)}`;
}

function getRoomBadgeText(roomCount: number) {
  return `룸 ${roomCount}개`;
}

function SlotLegend() {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-1.5">
        <div className="w-4 h-4 rounded bg-white border border-gray-300" />
        <span className="text-[11px] text-[#6B7280]">가능</span>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="w-4 h-4 rounded bg-gray-200 border border-gray-200" />
        <span className="text-[11px] text-[#6B7280]">불가</span>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="w-4 h-4 rounded bg-gray-400 border border-gray-400" />
        <span className="text-[11px] text-[#6B7280]">운영 외</span>
      </div>
    </div>
  );
}

function ShopListItem({
  shop,
  isSelected,
  onClick,
  compact = false,
}: {
  shop: Shop;
  isSelected: boolean;
  onClick: () => void;
  compact?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left transition-colors cursor-pointer border-b border-[#F3F4F6] last:border-b-0 ${
        compact ? 'px-4 py-3.5' : 'px-4 py-4'
      } ${
        isSelected
          ? 'bg-[#EFF6FF] border-l-[3px] border-l-[#2563EB]'
          : 'hover:bg-[#F9FAFB] border-l-[3px] border-l-transparent'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p
            className={`font-semibold truncate ${
              compact ? 'text-sm' : 'text-[15px]'
            } ${isSelected ? 'text-[#1E3A8A]' : 'text-[#111827]'}`}
          >
            {shop.shopName}
          </p>
          <p className="text-[11px] text-[#9CA3AF] mt-0.5 truncate">{shop.address}</p>
          <p className="text-[11px] text-[#9CA3AF] mt-0.5 truncate">
            {getRoomBadgeText(shop.roomCount || 0)}
          </p>
        </div>

        {isSelected && (
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-check-line text-[#2563EB] text-sm" />
          </div>
        )}
      </div>
    </button>
  );
}

function WeekViewPanel({
  shop,
  selectedShopId
}: {
  shop: { id: number; shopName: string } | null;
  selectedShopId: number | null | undefined
}) {
  const shopId = shop?.id;

  const {
    operationPolicy,
    roomFilter,
    setRoomFilter,
    currentWeekStart,
    setCurrentWeekStart,
    openSlotStatusMap,
    bookedStatusMap,
    isPolicyLoading,
    isWeekLoading,
    sessionMinutes,
    rooms,
    weekDates,
    filteredRooms,
    timeAxis,
  } = useWeeklyScheduleBase({
    shopId: shopId || selectedShopId,
  } as any);
  const previousWeek = () => {
    const d = new Date(currentWeekStart);
    d.setDate(d.getDate() - 7);
    setCurrentWeekStart(d);
  };

  const nextWeek = () => {
    const d = new Date(currentWeekStart);
    d.setDate(d.getDate() + 7);
    setCurrentWeekStart(d);
  };
  const onToday = () => {
    const d = new Date();
    setCurrentWeekStart(d);
  }
  if (!shopId) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-8 py-20">
        <div className="w-16 h-16 flex items-center justify-center bg-[#F3F4F6] rounded-2xl mb-4">
          <i className="ri-store-2-line text-2xl text-[#9CA3AF]" />
        </div>
        <p className="text-sm font-semibold text-[#374151]">업체를 선택해주세요</p>
      </div>
    );
  }

  if (isPolicyLoading) {
    return (
      <div className="flex items-center justify-center h-full px-8 py-20">
        <div className="text-sm text-[#6B7280]">운영 정책을 불러오는 중입니다...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-shrink-0 px-5 py-3.5 border-b border-[#E5E7EB] flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <button
              onClick={previousWeek}
              className="w-8 h-8 flex items-center justify-center hover:bg-[#F3F4F6] rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-lg text-[#374151]" />
            </button>

            <span className="text-sm font-semibold text-[#111827] text-center whitespace-nowrap px-1" style={{minWidth: 100}}>
              {formatDateRange(currentWeekStart)}
            </span>

            <button
              onClick={nextWeek}
              className="w-8 h-8 flex items-center justify-center hover:bg-[#F3F4F6] rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-lg text-[#374151]" />
            </button>
          </div>

          <button
            onClick={onToday}
            className="px-3 py-1.5 text-xs font-medium text-[#2563EB] bg-[#EFF6FF] rounded-lg hover:bg-[#DBEAFE] transition-colors cursor-pointer whitespace-nowrap"
          >
            오늘
          </button>
        </div>

        <div className="flex items-center gap-2" style={{display: 'none'}}>
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg">
            <i className="ri-eye-line text-[#9CA3AF] text-sm" />
            <span className="text-xs text-[#9CA3AF] font-medium whitespace-nowrap">조회 전용</span>
          </div>

          {/* <button
            onClick={onEdit}
            className="inline-flex items-center gap-2 rounded-xl bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#1D4ED8] cursor-pointer whitespace-nowrap"
          >
            <i className="ri-edit-line text-sm" />
            주간 일정 수정
          </button> */}
        </div>
      </div>

      <div className="flex-shrink-0 px-5 py-3 border-b border-[#F3F4F6] flex items-center gap-3">
        <div className="w-8 h-8 flex items-center justify-center bg-[#EFF6FF] rounded-lg flex-shrink-0">
          <i className="ri-store-2-line text-[#2563EB] text-sm" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-[#111827] truncate">{shop.shopName}</p>
          <p className="text-xs text-[#9CA3AF] truncate">
            세션 단위 {sessionMinutes}분
          </p>
        </div>
      </div>

      <div className="flex-shrink-0 px-5 py-3 border-b border-[#F3F4F6]">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium text-[#374151]">룸 필터</span>

          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setRoomFilter(null)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                roomFilter === null
                  ? 'bg-[#2563EB] text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              전체
            </button>

            {rooms.map((room: any, idx: number) => {
              const color = ROOM_COLOR_CONFIG[idx % ROOM_COLOR_CONFIG.length];

              return (
                <button
                  key={room.id}
                  onClick={() => setRoomFilter(String(room.id))}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    roomFilter === String(room.id)
                      ? color.filterActive
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {room.roomName}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto  bg-[#FAFAFA]">
        <div className="overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white">
          {/* {isWeekLoading ? (
            <div className="py-16 text-center text-sm text-gray-400">
              주간 오픈 슬롯을 불러오는 중입니다...
            </div>
          ) : ( */}
            <div className="overflow-x-auto p-3 md:p-4">
              <WeeklyScheduleGrid
                operationPolicy={operationPolicy}
                weekDates={weekDates}
                rooms={rooms}
                filteredRooms={filteredRooms}
                openSlotStatusMap={openSlotStatusMap}
                bookedStatusMap={bookedStatusMap}
                timeAxis={timeAxis}
                sessionMinutes={sessionMinutes}
              />
            </div>
          {/* )} */}
        </div>
      </div>

      <div className="flex-shrink-0 px-5 py-3 border-t border-[#F3F4F6] bg-white flex items-center justify-between gap-4 flex-wrap">
        <SlotLegend />
        <div className="text-xs text-[#9CA3AF]">
          현재 표시: {filteredRooms.map((r: any) => r.roomName).join(', ')} ({filteredRooms.length}개 룸)
        </div>
      </div>
    </div>
  );
}

function MobileSlotDrawer({
  isOpen,
  onClose,
  shop,
  onPrevWeek,
  onNextWeek,
  selectedShopId
}: {
  isOpen: boolean;
  onClose: () => void;
  shop: { id: number; shopName: string } | null;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onToday: () => void;
  selectedShopId: number | null
}) {
  const shopId = shop?.id;
  const {
    operationPolicy,
    roomFilter,
    setRoomFilter,
    currentWeekStart,
    setCurrentWeekStart,
    openSlotStatusMap,
    bookedStatusMap,
    isPolicyLoading,
    isWeekLoading,
    sessionMinutes,
    rooms,
    weekDates,
    filteredRooms,
    timeAxis,
  } = useWeeklyScheduleBase({
    shopId,
  } as any);
  const previousWeek = () => {
    const d = new Date(currentWeekStart);
    d.setDate(d.getDate() - 7);
    setCurrentWeekStart(d);
  };

  const nextWeek = () => {
    const d = new Date(currentWeekStart);
    d.setDate(d.getDate() + 7);
    setCurrentWeekStart(d);
  };
  const onToday = () => {
    const d = new Date();
    setCurrentWeekStart(d);
  }
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!shop) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex flex-col justify-end md:hidden transition-all duration-300 ${
        isOpen ? 'pointer-events-auto' : 'pointer-events-none'
      }`}
    >
      <div
        className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />

      <div
        className={`relative bg-white flex flex-col transition-transform duration-300 ease-out ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{ height: '100vh' }}
      >
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 bg-[#E5E7EB] rounded-full" />
        </div>

        <div className="px-5 pt-2 pb-3 border-b border-[#E5E7EB] flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 flex items-center justify-center bg-[#EFF6FF] rounded-xl flex-shrink-0">
                <i className="ri-store-2-line text-[#2563EB] text-base" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-bold text-[#111827] truncate">{shop.shopName}</h3>
              </div>
            </div>

            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center hover:bg-[#F3F4F6] rounded-xl cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line text-xl text-[#374151]" />
            </button>
          </div>
        </div>

        <div className="flex-shrink-0 px-4 py-2.5 border-b border-[#E5E7EB] flex items-center justify-between bg-[#FAFAFA]" style={{display: 'none'}}>
          <div className="flex items-center gap-1">
            <button
              onClick={previousWeek}
              className="w-9 h-9 flex items-center justify-center hover:bg-[#F3F4F6] rounded-lg cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-lg text-[#374151]" />
            </button>

            <span className="text-xs font-semibold text-[#111827] whitespace-nowrap px-1">
              {formatDateRange(currentWeekStart)}
            </span>

            <button
              onClick={nextWeek}
              className="w-9 h-9 flex items-center justify-center hover:bg-[#F3F4F6] rounded-lg cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-lg text-[#374151]" />
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onToday}
              className="px-3 py-1.5 text-xs font-semibold text-[#2563EB] bg-[#EFF6FF] rounded-lg hover:bg-[#DBEAFE] transition-colors cursor-pointer whitespace-nowrap"
            >
              오늘
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0">
          <WeekViewPanel
            selectedShopId={selectedShopId}
            shop={shop}
            currentWeekStart={currentWeekStart}
            onPrevWeek={onPrevWeek}
            onNextWeek={onNextWeek}
            onToday={onToday}
          />
        </div>
      </div>
    </div>
  );
}

export default function ShopCalendar() {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchWord, setSearchWord] = useState('')
  const shops = useSelector((state: any) => state.shop.shops || []);
  const loaded = useSelector((state: any) => state.shop.loaded);
  const loading = useSelector((state: any) => state.shop.loading);

  const selectedShopId = Number(searchParams.get('shopId') || 0) || null;
  const mobileOpen = searchParams.get('drawer') === '1';

  const currentWeekParam = searchParams.get('weekStart');
  const currentWeekStart = useMemo(() => {
    if (currentWeekParam) {
      const d = new Date(currentWeekParam);
      if (!Number.isNaN(d.getTime())) {
        return d;
      }
    }
    return getWeekStart(new Date());
  }, [currentWeekParam]);

  useEffect(() => {
    if (!loaded && !loading) {
      dispatch(fetchShops());
    }
  }, [dispatch, loaded, loading]);

  const filteredShops = useMemo(() => {
    const keyword = searchWord.trim().toLowerCase();
    if (!keyword) return shops;
    return shops.filter((shop: any) =>
      String(shop.shopName || '').toLowerCase().includes(keyword)
    );
  }, [shops, searchWord]);

  const selectedShop = useMemo(() => {
    if (!selectedShopId) return null;
    return shops.find((shop: any) => Number(shop.id) === Number(selectedShopId)) || null;
  }, [shops, selectedShopId]);

  const updateParams = (patch: Record<string, string | null>, options?: Object) => {
    const next = new URLSearchParams(searchParams);
    console.log(next)
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null || value === '') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });

    setSearchParams(next, options);
  };

  const handleSearchChange = (value: string) => {
    
  };

  const handlePrevWeek = () => {
    const d = new Date(currentWeekStart);
    d.setDate(d.getDate() - 7);
    updateParams({ weekStart: d.toISOString().split('T')[0] }, { replace: true});
  };

  const handleNextWeek = () => {
    const d = new Date(currentWeekStart);
    d.setDate(d.getDate() + 7);
    updateParams({ weekStart: d.toISOString().split('T')[0] }, { replace: true});
  };

  const handleToday = () => {
    const d = getWeekStart(new Date());
    updateParams({ weekStart: d.toISOString().split('T')[0] }, { replace: true});
  };

  const handleDesktopSelectShop = (shopId: number) => {
    updateParams({
      shopId: String(shopId),
      drawer: null,
    }, { replace: true });
  };

  const handleMobileSelectShop = (shopId: number) => {
    updateParams({
      shopId: String(shopId),
      drawer: '1',
    });
  };

  const handleCloseDrawer = () => {
    updateParams({ drawer: null });
  };

  // const handleEdit = () => {
  //   if (!selectedShopId) return;
  //   navigate(`/time-slot-generator?shopId=${selectedShopId}`);
  // };

  const SearchInput = (
    <div className="relative">
      <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] text-sm" />
      <input
        type="text"
        placeholder="업체명 검색"
        value={searchWord}
        onChange={(e) => setSearchWord(e.target.value)}
        className="w-full pl-9 pr-9 py-2.5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm focus:outline-none focus:border-[#2563EB] transition-all"
      />
      {searchWord && (
        <button
          onClick={() => setSearchWord('')}
          className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center cursor-pointer"
        >
          <i className="ri-close-line text-[#9CA3AF] text-sm" />
        </button>
      )}
    </div>
  );

  return (
    <>
      <div className="md:hidden min-h-screen bg-[#F5F6F8] flex flex-col">
        <div className="bg-white border-b border-[#E5E7EB] px-4 py-3.5 flex items-center gap-3 flex-shrink-0">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center hover:bg-[#F3F4F6] rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl text-[#374151]" />
          </button>

          <div className="flex-1 min-w-0">
            <p className="text-[11px] text-[#9CA3AF] font-medium tracking-wide uppercase">
              슬롯 조회
            </p>
            <h2 className="text-base font-bold text-[#111827] leading-tight">
              업체별 슬롯 보기
            </h2>
          </div>

          <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg">
            <i className="ri-eye-line text-[#9CA3AF] text-xs" />
            {/* <span className="text-[10px] text-[#9CA3AF] font-medium whitespace-nowrap">조회 전용</span> */}
          </div>
        </div>

        <div className="bg-white border-b border-[#E5E7EB] px-4 py-3 flex-shrink-0">
          {SearchInput}
          <p className="text-[11px] text-[#9CA3AF] mt-2">
            {loading ? '업체 불러오는 중...' : `${filteredShops.length}개 업체`}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto bg-white">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-8">
              <div className="text-sm text-[#6B7280]">업체 목록을 불러오는 중입니다...</div>
            </div>
          ) : filteredShops.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-8">
              <div className="w-12 h-12 flex items-center justify-center bg-[#F3F4F6] rounded-xl mb-3">
                <i className="ri-search-line text-xl text-[#9CA3AF]" />
              </div>
              <p className="text-sm font-semibold text-[#374151]">검색 결과가 없습니다</p>
            </div>
          ) : (
            filteredShops.map((shop: any) => (
              <ShopListItem
                key={shop.id}
                shop={shop}
                isSelected={selectedShopId === shop.id}
                onClick={() => handleMobileSelectShop(shop.id)}
              />
            ))
          )}
        </div>
      </div>

      <div className="hidden md:flex flex-col h-screen bg-[#F5F6F8]">
        <div className="bg-white border-b border-[#E5E7EB] px-5 py-4 flex items-center gap-3 flex-shrink-0">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center hover:bg-[#F3F4F6] rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl text-[#374151]" />
          </button>

          <div>
            <p className="text-[11px] text-[#9CA3AF] font-medium tracking-wide uppercase">
              슬롯 조회
            </p>
            <h2 className="text-base font-bold text-[#111827] leading-tight">
              업체별 슬롯 보기
            </h2>
          </div>

          {/* <div className="ml-auto flex items-center gap-2 px-3 py-1.5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg">
            <i className="ri-eye-line text-[#9CA3AF] text-sm" />
            <span className="text-xs text-[#9CA3AF] font-medium">조회 전용 화면입니다</span>
          </div> */}
        </div>

        <div className="flex flex-1 min-h-0">
          <div className="w-72 flex-shrink-0 flex flex-col bg-white border-r border-[#E5E7EB]">
            <div className="px-4 py-3.5 border-b border-[#E5E7EB] flex-shrink-0">
              <p className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-widest mb-2">
                드레스샵 목록
              </p>
              {SearchInput}
              <p className="text-[11px] text-[#9CA3AF] mt-2">
                {loading ? '업체 불러오는 중...' : `${filteredShops.length}개 업체`}
              </p>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-12 text-center px-4">
                  <p className="text-sm text-[#6B7280]">업체 목록을 불러오는 중입니다...</p>
                </div>
              ) : filteredShops.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center px-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#F3F4F6] rounded-xl mb-3">
                    <i className="ri-search-line text-lg text-[#9CA3AF]" />
                  </div>
                  <p className="text-sm text-[#6B7280]">검색 결과가 없습니다</p>
                </div>
              ) : (
                filteredShops.map((shop: any) => (
                  <ShopListItem
                    key={shop.id}
                    shop={shop}
                    isSelected={selectedShopId === shop.id}
                    onClick={() => handleDesktopSelectShop(shop.id)}
                    compact
                  />
                ))
              )}
            </div>
          </div>

          <div className="flex-1 flex flex-col bg-white min-w-0">
            <WeekViewPanel
              selectedShopId={selectedShopId}
              shop={selectedShop}
              currentWeekStart={currentWeekStart}
              onPrevWeek={handlePrevWeek}
              onNextWeek={handleNextWeek}
              onToday={handleToday}
            />
          </div>
        </div>
      </div>
      {
        mobileOpen && (
          <MobileSlotDrawer
            isOpen={mobileOpen}
            onClose={handleCloseDrawer}
            shop={selectedShop}
            currentWeekStart={currentWeekStart}
            onPrevWeek={handlePrevWeek}
            onNextWeek={handleNextWeek}
            onToday={handleToday}
          />
        )
      }
    </>
  );
}